'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';

const BenefitsSection = () => {
  const { t } = useLanguage();
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
  const sectionRef = useRef<HTMLElement>(null);

  const benefits = [
    { icon: 'UserCircleIcon', key: 'professionalProfile', span: 'col-span-1 md:col-span-1' },
    { icon: 'ComputerDesktopIcon', key: 'personalPage', span: 'col-span-1 md:col-span-1' },
    { icon: 'LinkIcon', key: 'referralLink', span: 'col-span-1 md:col-span-1' },
    { icon: 'AcademicCapIcon', key: 'training', span: 'col-span-1 md:col-span-1' },
    { icon: 'MegaphoneIcon', key: 'marketing', span: 'col-span-1 md:col-span-2' },
    { icon: 'GlobeAltIcon', key: 'community', span: 'col-span-1 md:col-span-1' },
    { icon: 'SparklesIcon', key: 'connect', span: 'col-span-1 md:col-span-1' },
    { icon: 'ChatBubbleLeftRightIcon', key: 'support', span: 'col-span-1 md:col-span-1' },
    { icon: 'ChartBarIcon', key: 'dashboard', span: 'col-span-1 md:col-span-2' },
    { icon: 'WrenchScrewdriverIcon', key: 'digitalTools', span: 'col-span-1 md:col-span-1' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setTimeout(() => {
              setVisibleItems((prev) => new Set([...prev, index]));
            }, index * 80);
          }
        });
      },
      { threshold: 0.1 }
    );

    const items = sectionRef.current?.querySelectorAll('[data-index]');
    items?.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="beneficios" className="py-32 bg-[#F4F7FC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-2xl mb-20">
          <div className="inline-flex items-center gap-2 mb-6">
            <div className="w-1.5 h-1.5 rounded-full bg-[#1D6FE8]" />
            <span className="text-sm font-body font-medium text-[#1D6FE8] tracking-wide uppercase">
              {t('benefits.eyebrow')}
            </span>
          </div>
          <h2 className="font-display font-bold text-[#0A1628] leading-tight tracking-tight mb-6"
            style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
            {t('benefits.headline')}{' '}
            <span className="gradient-text-blue">{t('benefits.headlineAccent')}</span>
          </h2>
          <p className="font-body text-lg text-[#4A5568] leading-relaxed">
            {t('benefits.subtitle')}
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {benefits.map((benefit, index) => (
            <div
              key={benefit.key}
              data-index={index}
              className={`${benefit.span} group bg-white rounded-2xl p-6 border border-[#E8EDF5] hover-lift cursor-default transition-all duration-350 ${
                visibleItems.has(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#EEF4FD] flex items-center justify-center flex-shrink-0 group-hover:bg-[#1D6FE8] transition-colors duration-350">
                  <Icon
                    name={benefit.icon as any}
                    size={20}
                    className="text-[#1D6FE8] group-hover:text-white transition-colors duration-350"
                  />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-[#0A1628] mb-1.5 text-base">
                    {t(`benefits.${benefit.key}`)}
                  </h3>
                  <p className="font-body text-sm text-[#4A5568] leading-relaxed">
                    {t(`benefits.${benefit.key}Desc`)}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
